import { useEffect, useRef, useState } from "react";
import { CHART_TOKENS } from "./theme";
import "./DateRangePicker.css";

// Range preset → (daysBack, bucket) pairs. `bucket` is passed straight to
// the backend time-bucketer and chooses a sensible granularity for the range.
// ``short`` renders on phones (CSS swaps it in under 640px) so the six
// segments fit one row instead of wrapping to two lines.
export const RANGE_PRESETS = [
  { key: "7d", label: "7 days", short: "7D", days: 7, bucket: "day" },
  { key: "30d", label: "30 days", short: "30D", days: 30, bucket: "day" },
  { key: "90d", label: "90 days", short: "90D", days: 90, bucket: "week" },
  { key: "1y", label: "1 year", short: "1Y", days: 365, bucket: "week" },
  { key: "all", label: "All time", short: "All", days: null, bucket: "month" },
];

export const DEFAULT_RANGE = RANGE_PRESETS[1]; // 30d

// Compact segmented-control-style range picker. Pass the currently-selected
// preset key and a callback; state lives in the parent so the picker is
// shareable across multiple dashboards on the same page.
const fmtShort = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
};

export const DateRangePicker = ({ value = "30d", onChange, align = "right", custom = false }) => {
  const [customOpen, setCustomOpen] = useState(false);
  const popRef = useRef();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (!customOpen) return;
    const handler = (e) => {
      if (!popRef.current?.contains(e.target)) setCustomOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [customOpen]);

  const applyCustom = () => {
    if (!from || !to) return;
    onChange?.({ key: "custom", days: null, bucket: "day", from, to });
    setCustomOpen(false);
  };

  return (
    <div
      className="afri-range-picker"
      style={{
        position: "relative",
        display: "inline-flex",
        background: CHART_TOKENS.track,
        borderRadius: 8,
        padding: 3,
        maxWidth: "100%",
      }}
    >
      {RANGE_PRESETS.map((r) => {
        const active = r.key === value;
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onChange?.(r)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: "var(--font-size-caption)",
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
              background: active ? "var(--color-neutral-background)" : "transparent",
              color: active ? CHART_TOKENS.labelStrong : CHART_TOKENS.label,
              boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
            }}
            aria-pressed={active}
            className={active ? "is-active" : undefined}
            aria-label={r.label}
            title={r.label}
          >
            <span className="afri-range-picker__long">{r.label}</span>
            <span className="afri-range-picker__short" aria-hidden>
              {r.short}
            </span>
          </button>
        );
      })}
      {custom && (
        <>
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            aria-pressed={value === "custom"}
            className={value === "custom" ? "is-active" : undefined}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: "var(--font-size-caption)",
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
              background: value === "custom" ? "var(--color-neutral-background)" : "transparent",
              color: value === "custom" ? CHART_TOKENS.labelStrong : CHART_TOKENS.label,
            }}
          >
            <span className="afri-range-picker__long">Custom…</span>
            <span className="afri-range-picker__short" aria-hidden>
              ⋯
            </span>
          </button>
          {customOpen && (
            <div
              ref={popRef}
              className="afri-range-picker__pop"
              style={{
                position: "absolute",
                top: "100%",
                [align]: 0,
                marginTop: 6,
                background: "var(--color-neutral-background)",
                border: "1px solid var(--color-neutral-border)",
                borderRadius: 8,
                padding: 12,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                display: "flex",
                gap: 8,
                alignItems: "center",
                zIndex: 5,
              }}
            >
              {/* Native date inputs ignore ``placeholder``; a visible caption
                  is the only reliable way to say which is which. */}
              {/* Native date input for the picker (tap → calendar), but the
                  visible text is OUR compact "dd/mm/yy" overlay — a native
                  input always renders the locale's full year and can't be
                  shortened with CSS. The input sits on top, transparent
                  text, so the tap target is unchanged. */}
              {[
                ["From", from, setFrom],
                ["To", to, setTo],
              ].map(([label, val, set]) => (
                <label key={label} className="afri-range-picker__field">
                  <span>{label}</span>
                  <span className="afri-range-picker__display" aria-hidden>
                    {val ? fmtShort(val) : "dd/mm/yy"}
                  </span>
                  <input type="date" value={val} onChange={(e) => set(e.target.value)} aria-label={`${label} date`} />
                </label>
              ))}
              <button type="button" className="afri-range-picker__apply" onClick={applyCustom} disabled={!from || !to}>
                Apply
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
